
import React from "react";

const sections = [
  {
    title: "1. Introduction",
    content: (
      <p>
        Welcome to Anisso ("we", "our", or "us"). Anisso is an application
        that helps users discover local events and activities. This Privacy
        Policy explains how we collect, use, disclose, and protect your
        information when you use our application.
      </p>
    ),
  },
  {
    title: "2. Information We Collect",
    content: (
      <>
        <p>
          Depending on the features you use, we may collect the following
          categories of information:
        </p>
        <ul>
          <li>
            <strong>Account information:</strong> Information you provide
            when registering or managing your account, such as your name,
            email address, and other profile details.
          </li>
          <li>
            <strong>Location information:</strong> Your device location,
            if you grant permission, to provide location-based features
            such as discovering nearby events.
          </li>
          <li>
            <strong>User content:</strong> Information, photos, event
            details, and other content you choose to upload or share.
          </li>
          <li>
            <strong>Technical information:</strong> Device type, operating
            system, app version, diagnostic information, and usage data
            where collected.
          </li>
          <li>
            <strong>Subscription information:</strong> Subscription status
            and transaction-related information required to manage access
            to premium features.
          </li>
        </ul>
        <p>
          The information actually collected depends on the features you
          use and the permissions you grant.
        </p>
      </>
    ),
  },
  {
    title: "3. How We Use Your Information",
    content: (
      <>
        <p>We may use your information to:</p>
        <ul>
          <li>Create, authenticate, and manage your account.</li>
          <li>Provide the application's features and services.</li>
          <li>Help you discover relevant events and activities.</li>
          <li>Manage subscriptions and premium features.</li>
          <li>Respond to questions and support requests.</li>
          <li>Maintain application security and prevent misuse.</li>
          <li>Diagnose technical problems and improve performance.</li>
          <li>Meet applicable legal obligations.</li>
        </ul>
      </>
    ),
  },
  {
    title: "4. Location Information",
    content: (
      <p>
        Anisso may request access to your device's location to provide
        location-based features, such as discovering nearby events.
        Location access is subject to your device permissions. You can
        disable location access through your device settings at any time.
        Some features may not function properly without location access.
      </p>
    ),
  },
  {
    title: "5. Sharing of Information",
    content: (
      <>
        <p>
          We do not sell your personal information. We may share information
          when necessary in the following circumstances:
        </p>
        <ul>
          <li>
            <strong>Service providers:</strong> With providers that help us
            operate the application, including hosting, databases,
            authentication, analytics, and subscription management, where
            applicable.
          </li>
          <li>
            <strong>Other users:</strong> Content and profile information
            that you choose to make public or share may be visible to other
            users.
          </li>
          <li>
            <strong>Legal requirements:</strong> When disclosure is required
            by law or necessary to protect rights, safety, or security.
          </li>
          <li>
            <strong>Business transfers:</strong> In connection with a
            merger, acquisition, or transfer of the service, subject to
            applicable law.
          </li>
        </ul>
      </>
    ),
  },
  {
    title: "6. Third-Party Services",
    content: (
      <p>
        Anisso may use third-party services to support application
        functionality, infrastructure, authentication, or subscription
        management. These providers may process information in accordance
        with their own privacy policies and applicable agreements. Where
        relevant, we encourage you to review their privacy policies.
      </p>
    ),
  },
  {
    title: "7. Data Retention and Security",
    content: (
      <p>
        We retain personal information only for as long as necessary for
        the purposes described in this policy, including providing our
        services, maintaining accounts, resolving disputes, and meeting
        legal obligations. We use reasonable technical and organizational
        measures to protect information against unauthorized access, loss,
        alteration, or disclosure. However, no electronic storage or
        transmission method can be guaranteed to be completely secure.
      </p>
    ),
  },
  {
    title: "8. Your Privacy Rights",
    content: (
      <p>
        Depending on your location and applicable law, you may have the
        right to access, correct, delete, or obtain a copy of your personal
        information, and to request restrictions on or object to certain
        processing. You may also withdraw device permissions at any time.
        To exercise your privacy rights, contact us at
        {" "}
        <a href="mailto:jamiity2023@gmail.com">
          jamiity2023@gmail.com
        </a>.
      </p>
    ),
  },
  {
    title: "9. Account Deletion",
    content: (
      <p>
        You can request deletion of your Anisso account and associated
        personal data through our account deletion page:
        {" "}
        <a
          href="https://anisso.org/dashboard/delete-account"
          target="_blank"
          rel="noopener noreferrer"
        >
          https://anisso.org/dashboard/delete-account
        </a>.
        {" "}
        Please follow the instructions provided on that page to submit
        your request. We will process valid requests in accordance with
        applicable law. Certain information may be retained where legally
        required or permitted, including for security, fraud prevention,
        or compliance with legal obligations.
      </p>
    ),
  },
  {
    title: "10. Children's Privacy",
    content: (
      <p>
        Anisso is not intended for children who are not permitted to use
        the service under applicable law. We do not knowingly collect
        personal information from children in violation of applicable
        legal requirements. If you believe that a child has provided
        personal information unlawfully, please contact us so that we
        can review the situation and take appropriate action.
      </p>
    ),
  },
  {
    title: "11. International Data Transfers",
    content: (
      <p>
        Depending on the location of our service providers, your information
        may be processed in countries other than your country of residence.
        Where required by applicable law, appropriate safeguards will be
        used for international data transfers.
      </p>
    ),
  },
  {
    title: "12. Changes to This Privacy Policy",
    content: (
      <p>
        We may update this Privacy Policy from time to time. Any changes
        will be published on this page along with a revised "Last updated"
        date. We encourage you to review this page periodically.
      </p>
    ),
  },
  {
    title: "13. Contact Us",
    content: (
      <p>
        If you have questions about this Privacy Policy or wish to exercise
        your privacy rights, you can contact us using the details below:
        <br />
        <strong>Application:</strong> Anisso
        <br />
        <strong>Email:</strong>{" "}
        <a href="mailto:jamiity2023@gmail.com">
          jamiity2023@gmail.com
        </a>
      </p>
    ),
  },
];

export default function PrivacyPolicy() {
  return (
    <div style={styles.page}>
      <header style={styles.header}>
        <div style={styles.headerInner}>
          <a href="/" style={styles.brand}>
            <span style={styles.logo}>A</span>
            <span>Anisso</span>
          </a>

          <span style={styles.headerLabel}>
            PRIVACY &amp; SECURITY
          </span>
        </div>
      </header>

      <main style={styles.main}>
        <div style={styles.intro}>
          <div style={styles.eyebrow}>LEGAL INFORMATION</div>

          <h1 style={styles.title}>Privacy Policy</h1>

          <p style={styles.subtitle}>
            Your privacy matters. Learn how Anisso collects, uses,
            and protects your information.
          </p>

          <div style={styles.updated}>
            Last updated: October 17, 2026
          </div>
        </div>

        <article style={styles.card}>
          {sections.map((section, index) => (
            <section key={section.title} style={styles.section}>
              <h2 style={styles.sectionTitle}>
                {section.title}
              </h2>

              <div style={styles.content}>
                {section.content}
              </div>

              {index !== sections.length - 1 && (
                <div style={styles.divider} />
              )}
            </section>
          ))}
        </article>

        <footer style={styles.footer}>
          <div style={styles.footerBrand}>Anisso</div>

          <p>
            Discover events. Connect with your community.
          </p>

          <div style={styles.footerLinks}>
            <a
              href="https://anisso.org/dashboard/delete-account"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.footerLink}
            >
              Delete Account
            </a>

            <span style={styles.linkSeparator}>·</span>

            <a
              href="https://play.google.com/store/apps/details?id=com.hanenouahabi.anisso"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.footerLink}
            >
              Google Play
            </a>

            <span style={styles.linkSeparator}>·</span>

            <a
              href="mailto:jamiity2023@gmail.com"
              style={styles.footerLink}
            >
              Contact Us
            </a>
          </div>

          <p style={styles.copyright}>
            © {new Date().getFullYear()} Anisso. All rights reserved.
          </p>
        </footer>
      </main>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    background: "#f7f8fc",
    color: "#20253a",
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    lineHeight: 1.75,
  },

  header: {
    background: "#ffffff",
    borderBottom: "1px solid #e9eaf2",
  },

  headerInner: {
    maxWidth: 900,
    margin: "0 auto",
    padding: "18px 24px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 16,
    flexWrap: "wrap",
  },

  brand: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    fontWeight: 750,
    fontSize: 21,
    color: "#252943",
    textDecoration: "none",
  },

  logo: {
    width: 38,
    height: 38,
    display: "grid",
    placeItems: "center",
    borderRadius: 12,
    background: "#6657d9",
    color: "#ffffff",
    fontSize: 22,
  },

  headerLabel: {
    color: "#777c93",
    fontSize: 11,
    letterSpacing: 1.5,
    fontWeight: 700,
  },

  main: {
    maxWidth: 820,
    margin: "0 auto",
    padding: "54px 20px 30px",
  },

  intro: {
    marginBottom: 30,
  },

  eyebrow: {
    color: "#6657d9",
    fontSize: 12,
    fontWeight: 750,
    letterSpacing: 1.8,
  },

  title: {
    fontSize: "clamp(32px, 6vw, 46px)",
    letterSpacing: -1.5,
    lineHeight: 1.2,
    margin: "12px 0",
    color: "#20253a",
  },

  subtitle: {
    color: "#666d83",
    fontSize: 16,
    maxWidth: 610,
  },

  updated: {
    display: "inline-block",
    marginTop: 10,
    padding: "6px 12px",
    borderRadius: 8,
    background: "#eeecff",
    color: "#5949c1",
    fontSize: 12,
    fontWeight: 650,
  },

  card: {
    background: "#ffffff",
    border: "1px solid #ececf3",
    borderRadius: 18,
    padding: "10px 30px",
    boxShadow: "0 8px 30px rgba(30, 35, 70, 0.035)",
  },

  section: {
    paddingTop: 24,
  },

  sectionTitle: {
    fontSize: 18,
    lineHeight: 1.5,
    margin: "0 0 12px",
    fontWeight: 750,
    color: "#252943",
  },

  content: {
    fontSize: 14,
    color: "#60667a",
    overflowWrap: "anywhere",
  },

  divider: {
    height: 1,
    background: "#eeeff5",
    marginTop: 24,
  },

  footer: {
    textAlign: "center",
    padding: "36px 10px 10px",
    color: "#777c90",
    fontSize: 13,
  },

  footerBrand: {
    color: "#252943",
    fontSize: 20,
    fontWeight: 750,
  },

  footerLinks: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 10,
    marginTop: 18,
  },

  footerLink: {
    color: "#6657d9",
    textDecoration: "none",
    fontWeight: 650,
  },

  linkSeparator: {
    color: "#b5b7c5",
  },

  copyright: {
    marginTop: 24,
    fontSize: 12,
  },
};
